import { tracking } from 'App/Tracking';
import { usePractitioners } from 'Book/shared/hooks';
import { FILTER_CATEGORY_BY_KEY } from 'Book/shared/utils';
import { Direction, useInfiniteScroll } from 'hooks';
import React, { MutableRefObject, useRef } from 'react';
import { practitionersQuery_practitioners_edges_node as PractitionersEdgeNode } from 'Services/BeGateway/Practitioner/__generated__/practitionersQuery';
import { PractitionerCardListProps } from '../PractitionerCardList';
import { PractitionerCardListBase } from './PractitionerCardListBase';

export const StandardPractitionerCardList = ({
  filters = {
    languages: [],
    areasOfExpertise: [],
    approaches: [],
    genders: [],
    ethnicities: [],
  },
  limit,
  trackingEvent,
  lastAddedFilterKey,
  onRemoveFilter,
}: Omit<PractitionerCardListProps, 'featured'>) => {
  const pageRef: MutableRefObject<null> = useRef(null);

  const { data, fetchMorePractitioners, loading, error, refetch } =
    usePractitioners({
      practitionersQueryWhere: {
        languages: {
          in: [...filters?.languages?.map(s => s.value)],
        },
        ...(filters.genders.length && {
          gender: {
            in: [...filters.genders.map(s => s.value)],
          },
        }),
        ...(filters.ethnicities.length && {
          ethnicity: {
            in: [...filters.ethnicities.map(s => s.value)],
          },
        }),
        userUserProfileDetailConnects: {
          ...(filters.approaches.length && {
            approaches: {
              in: [...filters?.approaches?.map(s => s.value)],
            },
          }),
          ...(filters.areasOfExpertise.length && {
            and: {
              areasOfExpertise: {
                in: [...filters?.areasOfExpertise?.map(s => s.value)],
              },
            },
          }),
        },
      },
      limit,
    });

  useInfiniteScroll(
    () => {
      if (data?.practitioners?.pageInfo?.hasNextPage) {
        fetchMorePractitioners();
      }
    },
    pageRef,
    Direction.DOWN,
    500,
    !limit,
  );

  const noFiltersApplied = Object.values(filters).every(
    filter => filter.length === 0,
  );

  const removeLastAddedFilter =
    lastAddedFilterKey && onRemoveFilter
      ? () => {
          tracking.track('talk-browse-practitioner-filter-option-selected', {
            filterOption: 'all',
            filterCategory: FILTER_CATEGORY_BY_KEY[lastAddedFilterKey],
            action: 'deselect',
            uiElement: 'empty-state',
          });
          onRemoveFilter(lastAddedFilterKey);
        }
      : undefined;

  const practitioners: PractitionersEdgeNode[] =
    (data?.practitioners?.edges
      ?.map(edge => edge?.node)
      .filter(Boolean) as PractitionersEdgeNode[]) ?? [];

  return (
    <PractitionerCardListBase
      trackingEvent={trackingEvent}
      practitioners={practitioners}
      hasFilters={!noFiltersApplied}
      removableFilterKey={lastAddedFilterKey}
      onRemoveFilter={removeLastAddedFilter}
      loading={loading}
      error={!!error}
      refetch={refetch}
    />
  );
};
